import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getClothingRules, createClothingRule, updateClothingRule, deleteClothingRule, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaTrash, FaTshirt } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function ClothingRules({ user }) {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    rule_text: '',
    category: '',
    active: true
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await getClothingRules();
      setRules(data.rules || data);
    } catch (error) {
      console.error('Error fetching clothing rules:', error);
      toast.error(extractErrorMessage(error, 'Kleiderregeln konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRule(null);
    setFormData({ user_id: '', rule_text: '', category: '', active: true });
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      user_id: rule.user_id || '',
      rule_text: rule.rule_text || '',
      category: rule.category || '',
      active: rule.active !== undefined ? rule.active : true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      if (editingRule) {
        await updateClothingRule(editingRule.id, formData);
      } else {
        await createClothingRule(formData);
      }
      setShowModal(false);
      setEditingRule(null);
      setFormData({ user_id: '', rule_text: '', category: '', active: true });
      toast.success(editingRule ? 'Kleiderregel aktualisiert' : 'Kleiderregel erstellt');
      fetchRules();
    } catch (error) {
      console.error('Error saving clothing rule:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern der Kleiderregel'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await deleteClothingRule(id);
      toast.success('Kleiderregel gelöscht');
      fetchRules();
    } catch (error) {
      console.error('Error deleting clothing rule:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Kleiderregel'));
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await updateClothingRule(rule.id, { ...rule, active: !rule.active });
      toast.success(rule.active ? 'Regel deaktiviert' : 'Regel aktiviert');
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Umschalten der Regel'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaTshirt /> Clothing Rules</h1>
          <button className="add-btn" onClick={openCreateModal}>
            <FaPlus /> New Rule
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Rule</th>
                  <th>Category</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.length > 0 ? (
                  rules.map(rule => (
                    <tr key={rule.id} style={{ opacity: rule.active ? 1 : 0.5 }}>
                      <td>{rule.id}</td>
                      <td>{rule.user_id || '-'}</td>
                      <td>{rule.rule_text}</td>
                      <td>{rule.category || '-'}</td>
                      <td>
                        <button
                          className={`toggle-btn ${rule.active ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleActive(rule)}
                          style={{
                            background: rule.active ? '#43b581' : '#f04747',
                            color: '#fff',
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {rule.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td>
                        <button className="edit-btn" onClick={() => openEditModal(rule)}>
                          <FaEdit />
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(rule.id)}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-data">No clothing rules defined</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingRule ? 'Edit Clothing Rule' : 'New Clothing Rule'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <UserSelect
                    value={formData.user_id}
                    onChange={id => setFormData({...formData, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Rule Text</label>
                  <textarea
                    value={formData.rule_text}
                    onChange={e => setFormData({...formData, rule_text: e.target.value})}
                    placeholder="Describe the clothing rule..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., daily, special, outdoor"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={e => setFormData({...formData, active: e.target.checked})}
                    />
                    {' '}Active
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert\u2026' : (editingRule ? 'Update' : 'Create')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ClothingRules;
